/**
 * Coach Voice TTS — provider dispatcher.
 *
 * Routes speak() calls to one of three providers based on user settings:
 *   - browser:    Web Speech API (free, varying quality by host)
 *   - openai:     OpenAI TTS API (very natural, ~$0.005-$0.015 per paragraph)
 *   - elevenlabs: ElevenLabs API (best-in-class, ~$0.02-$0.05 per paragraph)
 *
 * Public API kept stable so existing callers (CoachSpeakButton etc.) don't
 * need to change — they just pass `settings` now in addition to `voice`.
 *
 * State management:
 *   - Single global "is speaking" flag (one stream at a time)
 *   - Subscribers get notified when state flips
 *   - stop() cancels whichever provider is currently active
 */

import * as browserTTS from './tts/browserTTS.js';
import * as openaiTTS from './tts/openaiTTS.js';
import * as elevenlabsTTS from './tts/elevenlabsTTS.js';

export const TTS_PROVIDERS = ['browser', 'openai', 'elevenlabs'];

const PROVIDER_MAP = {
  browser: browserTTS,
  openai: openaiTTS,
  elevenlabs: elevenlabsTTS,
};

// Singleton speaking-state subscribers (shared across providers).
const _listeners = new Set();
let _speaking = false;
let _activeProvider = null;

const setSpeaking = (val, provider = null) => {
  if (val) {
    _speaking = true;
    _activeProvider = provider;
  } else {
    _speaking = false;
    _activeProvider = null;
  }
  for (const fn of _listeners) {
    try { fn(_speaking); } catch { /* listener bug shouldn't break TTS */ }
  }
};

export const subscribeToSpeakingState = (fn) => {
  if (typeof fn !== 'function') return () => {};
  _listeners.add(fn);
  try { fn(_speaking); } catch { /* */ }
  return () => _listeners.delete(fn);
};

export const isSpeaking = () => _speaking;

/**
 * Is ANY TTS provider available? UI uses this to grey out the toggle if
 * the browser doesn't even support SpeechSynthesis (rare on Electron, but
 * possible in headless / restricted contexts).
 */
export const isTTSAvailable = () => browserTTS.isAvailable();

/**
 * Is a SPECIFIC provider available + configured? Used by Settings to show
 * "Test voice" buttons only when the provider can actually be exercised.
 */
export const isProviderReady = (provider, settings = {}) => {
  if (provider === 'browser') return browserTTS.isAvailable();
  if (provider === 'openai') return openaiTTS.isAvailable() && !!settings.openaiApiKey;
  if (provider === 'elevenlabs') return elevenlabsTTS.isAvailable() && !!settings.elevenlabsApiKey;
  return false;
};

/**
 * Speak text using the user's chosen provider.
 *
 * @param {string} text
 * @param {Object} options
 * @param {string} [options.voice='mentor']     Personality id
 * @param {Object} [options.ttsSettings]        From settings.coachLLM.tts (see DEFAULT_LLM_SETTINGS)
 * @returns {Promise<{ok:true} | {ok:false, error:string}>}
 */
export const speak = async (text, options = {}) => {
  const tts = options.ttsSettings || {};
  const provider = TTS_PROVIDERS.includes(tts.provider) ? tts.provider : 'browser';
  const mod = PROVIDER_MAP[provider];
  if (!mod) {
    return { ok: false, error: `Unknown TTS provider: ${provider}` };
  }

  // Stop anything that's currently playing — single-stream contract across
  // all providers. Without this, switching providers mid-utterance could
  // leave the prior provider's audio dangling.
  if (_speaking && _activeProvider && _activeProvider !== provider) {
    const prior = PROVIDER_MAP[_activeProvider];
    if (prior?.stop) prior.stop();
  }

  // Build the per-provider options. Each provider only needs a subset.
  const providerOpts = {
    voice: options.voice,
    onStart: () => setSpeaking(true, provider),
    onEnd: () => setSpeaking(false),
  };
  if (provider === 'browser') {
    providerOpts.voiceURI = tts.browserVoiceURI;
  } else if (provider === 'openai') {
    providerOpts.voiceMap = tts.openaiVoiceMap;
    providerOpts.model = tts.openaiModel;
  } else if (provider === 'elevenlabs') {
    providerOpts.voiceMap = tts.elevenlabsVoiceMap;
    providerOpts.model = tts.elevenlabsModel;
  }

  return await mod.speak(text, providerOpts);
};

/**
 * Stop any current speech immediately. Safe to call from any state — it
 * cancels whichever provider is active.
 */
export const stop = () => {
  if (_activeProvider && PROVIDER_MAP[_activeProvider]?.stop) {
    PROVIDER_MAP[_activeProvider].stop();
  }
  // Belt + suspenders: cancel browser even if it wasn't the active provider
  // (Web Speech queue can drift independently).
  try { browserTTS.stop(); } catch { /* */ }
  setSpeaking(false);
};

/**
 * List browser voices for the Settings dropdown. Returns [] for non-browser
 * providers (their voice lists are hardcoded / pulled from the provider).
 */
export const listBrowserVoices = () => browserTTS.listVoices();

// Re-export VOICE_PROFILES so callers that just want the pitch/rate
// metadata don't have to import from the browser provider.
export { VOICE_PROFILES } from './tts/browserTTS.js';
